import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ChatPageState } from '../chat-page.state';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import { MibButtonComponent, MibIconsModule, MibTooltipDirective } from '@medinbox/angular';
import { LivekitService } from '../../../core/services/livekit.service';

/**
 * With overflow: hidden on the host, when its width collapses to 0px,
 * the <video> element is clipped to that 0-area box — IntersectionObserver
 * will correctly report zero intersection and adaptive stream will pause the subscription.
 */
@Component({
  selector: 'app-video-tab',
  imports: [MibButtonComponent, MibIconsModule, MibTooltipDirective],
  templateUrl: './video-tab.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'overflow-hidden' },
})
export class VideoTab implements AfterViewInit {
  protected readonly state = inject(ChatPageState);
  private readonly destroyRef = inject(DestroyRef);
  private readonly livekit = inject(LivekitService);

  private readonly previewVideo = viewChild<ElementRef<HTMLVideoElement>>('previewVideo');
  private readonly audioEl = viewChild<ElementRef<HTMLAudioElement>>('audioEl');
  private readonly remoteVideoTrack$ = toObservable(this.livekit.remoteVideoTrack);
  private readonly remoteAudioTrack$ = toObservable(this.livekit.remoteAudioTrack);

  readonly remoteAudioTrack = this.livekit.remoteAudioTrack;
  readonly isMuted = signal(true);

  readonly isMinimized = computed(
    () => !this.state.isSmall() && this.state.tabState().video === 'minimized',
  );
  readonly isFullscreen = computed(
    () => !this.state.isSmall() && this.state.tabState().video === 'fullscreen',
  );
  readonly isHidden = toSignal(toObservable(this.isMinimized).pipe(debounceTime(100)), {
    initialValue: false,
  });

  ngAfterViewInit(): void {
    this.remoteVideoTrack$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((track) => {
      const el = this.previewVideo()?.nativeElement;
      if (!el) return;
      if (track) {
        track.attach(el);
      } else {
        el.srcObject = null;
      }
    });

    this.remoteAudioTrack$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((track) => {
      const el = this.audioEl()?.nativeElement;
      if (!el) return;
      if (track) {
        track.attach(el);
        el.muted = this.isMuted();
      } else {
        el.srcObject = null;
      }
    });
  }

  toggleMute(): void {
    const next = !this.isMuted();
    this.isMuted.set(next);
    const el = this.audioEl()?.nativeElement;
    if (el) el.muted = next;
  }

  toggleTab(): void {
    this.state.toggleVideo();
  }
}
